/**
 * ToolCategory - Hierarchical tool organization
 *
 * Manages a category of tools with support for:
 * - Declarative construction (no registration methods)
 * - Nested sub-categories
 * - Path-based addressing
 * - Tool search (precise and semantic)
 */

export class ToolCategory {
  /**
   * Constructor - Declarative configuration
   * @param {Object} config
   * @param {string} config.id - Category ID (used in paths and display)
   * @param {string} config.description - Description of category and scope
   * @param {Array} config.tools - Array of {definition, implementation}
   * @param {Array} config.subCategories - Array of ToolCategory instances
   * @param {Object} config.searcher - Optional semantic searcher { search: Function }
   */
  constructor(config) {
    this.id = config.id
    this.description = config.description
    this.parent = null
    this._searcher = config.searcher || null

    // Initialize tools Map
    this.tools = new Map()
    for (const tool of config.tools || []) {
      this.tools.set(tool.definition.name, tool)
    }

    // Initialize sub-categories Map and set parent
    this.subCategories = new Map()
    for (const subCategory of config.subCategories || []) {
      subCategory.parent = this
      // Propagate searcher to subcategories if not set
      if (this._searcher && !subCategory._searcher) {
        subCategory._searcher = this._searcher
      }
      this.subCategories.set(subCategory.id, subCategory)
    }
  }

  /**
   * Get current category's full path
   * @returns {string} Path like "browser/tabs" or "notes", empty string for root
   */
  getPath() {
    // Root category (id is empty string)
    if (!this.id) {
      return ''
    }
    // Top-level category (no parent)
    if (!this.parent || !this.parent.id) {
      return this.id
    }
    // Nested category
    const parentPath = this.parent.getPath()
    return parentPath ? `${parentPath}/${this.id}` : this.id
  }

  /**
   * Get semantic searcher (from config or parent)
   * @returns {{ search: Function, getCacheStats: Function }|null}
   */
  getSemanticSearch() {
    return this._searcher || this.parent?._searcher || null
  }

  /**
   * List all tools with full paths
   * @returns {Array<Object>} Tool definitions with paths
   */
  listTools() {
    const categoryPath = this.getPath()
    return Array.from(this.tools.entries()).map(([name, tool]) => ({
      ...tool.definition,
      path: categoryPath ? `${categoryPath}/${name}` : name,
      categoryPath,
    }))
  }

  /**
   * List all sub-categories with paths
   * @returns {Array<Object>} Sub-category info
   */
  listSubCategories() {
    return Array.from(this.subCategories.values()).map((c) => ({
      id: c.id,
      description: c.description,
      path: c.getPath(),
      hasTools: c.tools.size > 0,
      hasSubCategories: c.subCategories.size > 0,
    }))
  }

  /**
   * Execute a tool by name
   * @param {string} toolName - Tool name
   * @param {Object} ctx - Request context
   * @param {Object} args - Tool arguments
   * @returns {Promise<any>} Tool execution result
   */
  async execute(toolName, ctx, args) {
    const tool = this.tools.get(toolName)
    if (!tool) {
      throw new Error(
        `Tool '${toolName}' not found in category '${this.id}'`
      )
    }
    return await tool.implementation(ctx, args)
  }

  /**
   * Search tools in this category using semantic search
   * @param {string} query - Search query
   * @param {Object} options - Search options
   * @param {number} [options.minSimilarity=0] - Minimum similarity threshold
   * @returns {Promise<Array<Object>>} Matching tools with scores
   */
  async searchTools(query, options = {}) {
    const { minSimilarity = 0 } = options
    const categoryPath = this.getPath()

    const toolsArray = Array.from(this.tools.values()).map((tool) => ({
      name: tool.definition.name,
      path: categoryPath ? `${categoryPath}/${tool.definition.name}` : tool.definition.name,
      ...tool.definition,
    }))

    const searcher = this.getSemanticSearch()
    if (!searcher) {
      throw new Error(`Semantic search not available for category '${this.id}' (embedFn required)`)
    }

    // Convert tools to strings for search
    const strings = toolsArray.map((t) => `${t.name}\n${t.description || ''}`.trim())
    const results = await searcher.search(query, strings, {
      limit: toolsArray.length,
      minSimilarity,
    })

    return results.map((r) => ({
      ...toolsArray[r.index],
      score: r.score,
    }))
  }

  /**
   * Get category by path (for root category use)
   * @param {string} path - Category path like "notes" or "browser/content"
   * @returns {ToolCategory|null} Found category or null
   */
  getCategoryByPath(path) {
    if (!path) {
      return this // Return self for empty path
    }

    const parts = path.split('/')
    let current = this.subCategories.get(parts[0])
    if (!current) return null

    for (let i = 1; i < parts.length; i++) {
      current = current.subCategories.get(parts[i])
      if (!current) return null
    }

    return current
  }

  /**
   * Execute tool by full path (for root category use)
   * @param {string} toolPath - Tool path like "notes/saveNote"
   * @param {Object} ctx - Request context
   * @param {Object} args - Tool arguments
   * @returns {Promise<any>} Tool execution result
   */
  async executeByPath(toolPath, ctx, args) {
    const lastSlashIndex = toolPath.lastIndexOf('/')
    if (lastSlashIndex === -1) {
      throw new Error(
        `Invalid tool path: ${toolPath}. Expected format: category/tool`
      )
    }

    const categoryPath = toolPath.substring(0, lastSlashIndex)
    const toolName = toolPath.substring(lastSlashIndex + 1)

    const category = this.getCategoryByPath(categoryPath)
    if (!category) {
      throw new Error(`Category not found: ${categoryPath}`)
    }

    return await category.execute(toolName, ctx, args)
  }

  /**
   * Search tools recursively across this category and all subcategories
   * @param {string} query - Search query
   * @param {Object} options - Search options
   * @param {number} [options.minSimilarity=0] - Minimum similarity threshold
   * @returns {Promise<Array<Object>>} All matching tools sorted by score
   */
  async searchRecursively(query, options = {}) {
    const allResults = []

    const collectResults = async (category) => {
      const results = await category.searchTools(query, options)
      allResults.push(...results)

      // Recursively search subcategories
      for (const subCategory of category.subCategories.values()) {
        await collectResults(subCategory)
      }
    }

    await collectResults(this)
    return allResults.sort((a, b) => b.score - a.score)
  }
}
